import React, { useEffect, useRef } from 'react';
import './ai-button.css';

export interface AiButtonProps {
    size?: number;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
}

export const AiButton: React.FC<AiButtonProps> = ({
    size = 54,
    onClick,
    className,
    style,
}) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const stateRef = useRef<'flow10' | 'flow11' | 'flow12'>('flow10');

    function restartAnimationsForGroup(group: Element | null) {
        if (!group) return;
        if (typeof (group as any).getAnimations === 'function') {
            const animations: Animation[] = (group as any).getAnimations({
                subtree: true,
            });
            animations.forEach((a) => {
                try {
                    a.cancel();
                } catch (_) {
                    // Intentionally left empty to ignore animation errors
                }
            });
            animations.forEach((a) => {
                try {
                    a.play();
                } catch (_) {
                    // Intentionally left empty to ignore animation errors
                }
            });
            return;
        }
        const nodes = Array.from(group.querySelectorAll<HTMLElement>('*'));
        nodes.forEach((el) => {
            const cs = getComputedStyle(el);
            if (cs.animationName && cs.animationName !== 'none') {
                const { style: elementStyle } = el;
                elementStyle.animation = 'none';
            }
        });
        nodes.forEach((el) => {
            if (el.style.animation === 'none') {
                const elementStyle = el.style;
                elementStyle.animation = '';
            }
        });
    }

    function setState(next: 'flow10' | 'flow11' | 'flow12') {
        const svg = svgRef.current;
        if (!svg) return;
        if (stateRef.current === next) return; // CORRIGIDO
        svg.classList.remove('flow10', 'flow11', 'flow12');
        svg.classList.add(next);
        const group = svg.querySelector(`#${next}-group`);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => restartAnimationsForGroup(group));
        });
        stateRef.current = next;
    }

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        svg.classList.add('flow10');
        const initialGroup = svg.querySelector('#flow10-group');
        requestAnimationFrame(() => {
            requestAnimationFrame(() =>
                restartAnimationsForGroup(initialGroup)
            );
        });
    }, []);

    return (
        <div
            id="navia-ai-button"
            className={className}
            style={style}
            onClick={onClick}
            onMouseEnter={() => setState('flow11')}
            onMouseLeave={() => setState('flow12')}
            onFocus={() => setState('flow11')}
            onBlur={() => setState('flow12')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.();
                }
            }}
        >
            <svg
                ref={svgRef}
                id="navia-ai-logo"
                width={size}
                height={size}
                viewBox="0 0 54 54"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
            >
                <defs>
                    <clipPath id="clip0">
                        <rect width="54" height="54" rx="27" fill="white" />
                    </clipPath>
                </defs>
                <g clipPath="url(#clip0)">
                    <rect width="54" height="54" rx="27" fill="#3A8DDE" />

                    {/* FLOW10 */}
                    <g id="flow10-group" className="flow10">
                        <path
                            id="sparkle_bigger_1"
                            opacity="0"
                            d="M24.693 20.4643C25.2637 18.7061 27.5716 18.6528 28.2483 20.3046L28.3056 20.4654L29.0758 22.8365C29.2524 23.3803 29.5376 23.878 29.9123 24.2958C30.2871 24.7137 30.7426 25.0422 31.2482 25.259L31.4553 25.3404L33.7078 26.1502C35.3781 26.751 35.4287 29.1805 33.8605 29.8928L33.7078 29.9531L31.4553 30.7639C30.9385 30.9496 30.4656 31.2499 30.0685 31.6444C29.6713 32.0389 29.3592 32.5184 29.1531 33.0507L29.0758 33.2678L28.3065 35.64C27.7358 37.3983 25.4279 37.4515 24.7522 35.8007L24.693 35.64L23.9237 33.2688C23.7473 32.7248 23.4621 32.227 23.0873 31.8089C22.7126 31.3908 22.257 31.0622 21.7514 30.8453L21.5452 30.7639L19.2927 29.9541C17.6214 29.3533 17.5709 26.9238 19.14 26.2125L19.2927 26.1502L21.5452 25.3404C22.0618 25.1545 22.5345 24.8543 22.9315 24.4598C23.3284 24.0653 23.6404 23.5858 23.8464 23.0536L23.9237 22.8365L24.693 20.4643Z M26.4998 21.1134L25.7305 23.4846C25.4617 24.3138 25.0244 25.0714 24.4492 25.7046C23.8739 26.3377 23.1744 26.8313 22.3994 27.1509L22.1608 27.2423L19.9083 28.0522L22.1608 28.862C22.9485 29.1449 23.6682 29.6052 24.2697 30.2108C24.8712 30.8164 25.34 31.5527 25.6436 32.3685L25.7305 32.6197L26.4998 34.9909L27.2691 32.6197C27.5378 31.7905 27.9751 31.0329 28.5504 30.3997C29.1257 29.7666 29.8251 29.273 30.6001 28.9534L30.8387 28.863L33.0912 28.0522L30.8387 27.2423C30.051 26.9594 29.3313 26.4991 28.7298 25.8935C28.1284 25.2879 27.6595 24.5516 27.3559 23.7358L27.27 23.4846L26.4998 21.1134Z M34.1354 17C34.3139 17 34.4889 17.0527 34.6404 17.1522C34.7919 17.2517 34.9139 17.3939 34.9925 17.5627L35.0383 17.6802L35.3724 18.7111L36.3526 19.0627C36.5315 19.1267 36.6884 19.2452 36.8033 19.4032C36.9182 19.5611 36.986 19.7514 36.9981 19.9499C37.0101 20.1485 36.9659 20.3463 36.8711 20.5184C36.7762 20.6905 36.6349 20.829 36.4652 20.9165L36.3526 20.9647L35.3733 21.3164L35.0393 22.3482C34.9784 22.5366 34.8657 22.7016 34.7156 22.8225C34.5655 22.9433 34.3847 23.0146 34.1961 23.0272C34.0075 23.0398 33.8196 22.9931 33.6562 22.8932C33.4928 22.7932 33.3613 22.6445 33.2783 22.4658L33.2325 22.3482L32.8984 21.3174L31.9182 20.9657C31.7392 20.9017 31.5824 20.7832 31.4675 20.6253C31.3526 20.4673 31.2848 20.277 31.2727 20.0785C31.2607 19.88 31.3049 19.6821 31.3997 19.5101C31.4946 19.338 31.6358 19.1994 31.8056 19.112L31.9182 19.0637L32.8975 18.7121L33.2315 17.6802C33.2959 17.4817 33.4176 17.3094 33.5797 17.1874C33.7418 17.0654 33.9361 16.9999 34.1354 17Z"
                            fill="white"
                        />
                        <circle
                            id="Ellipse_69_2"
                            cx="11.42"
                            cy="5.71"
                            r="15.7432"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_70_3"
                            cx="23.98"
                            cy="51.06"
                            r="16.8036"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_71_4"
                            cx="54.29"
                            cy="21.98"
                            r="14.4789"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_68_5"
                            cx="37.36"
                            cy="35.4"
                            r="10.3595"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_72_6"
                            cx="29.57"
                            cy="2.24"
                            r="9.25831"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_73_7"
                            cx="20.39"
                            cy="19.41"
                            r="7.66767"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_74_8"
                            cx="8.4"
                            cy="38.91"
                            r="7.91239"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_75_9"
                            cx="8.32"
                            cy="31.81"
                            r="5.46526"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_76_10"
                            cx="41.32"
                            cy="16.27"
                            r="4.60876"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_77_11"
                            cx="37.36"
                            cy="49.27"
                            r="10.3595"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                    </g>

                    {/* FLOW11 */}
                    <g id="flow11-group" className="flow11">
                        <path
                            id="sparkle_bigger_11"
                            d="M24.276 20.549C24.8851 18.7666 27.3479 18.7126 28.07 20.387L28.1311 20.55L28.9531 22.9537C29.1414 23.5049 29.4458 24.0094 29.8457 24.433C30.2456 24.8566 30.7317 25.1895 31.2712 25.4093L31.4922 25.4918L33.8959 26.3128C35.6783 26.9218 35.7323 29.3846 34.0589 30.1067L33.8959 30.1679L31.4922 30.9898C30.9408 31.178 30.4361 31.4824 30.0123 31.8823C29.5885 32.2822 29.2555 32.7683 29.0356 33.3079L28.9531 33.5279L28.1321 35.9327C27.5231 37.7151 25.0603 37.7691 24.3392 36.0956L24.276 35.9327L23.4551 33.529C23.2669 32.9775 22.9625 32.4729 22.5626 32.0491C22.1627 31.6253 21.6766 31.2922 21.137 31.0723L20.917 30.9898L18.5132 30.1689C16.7298 29.5598 16.6758 27.097 18.3503 26.3759L18.5132 26.3128L20.917 25.4918C21.4682 25.3035 21.9727 24.9991 22.3963 24.5992C22.8199 24.1993 23.1528 23.7132 23.3726 23.1737L23.4551 22.9537L24.276 20.549Z"
                            fill="white"
                        />
                        <circle
                            id="Ellipse_69_1"
                            cx="-0.64"
                            cy="15.23"
                            r="17.4924"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_70_2"
                            cx="35.53"
                            cy="61.18"
                            r="18.6707"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_71_3"
                            cx="51.43"
                            cy="11.09"
                            r="16.0876"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_68_4"
                            cx="59.29"
                            cy="30.45"
                            r="11.5106"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_72_5"
                            cx="10.63"
                            cy="2.49"
                            r="10.287"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_73_6"
                            cx="13.77"
                            cy="12.68"
                            r="8.51964"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_74_7"
                            cx="18.22"
                            cy="52.12"
                            r="8.79154"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_75_8"
                            cx="9.24"
                            cy="44.24"
                            r="6.07251"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_76_9"
                            cx="37.02"
                            cy="4.75"
                            r="5.12085"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_77_10"
                            cx="50.4"
                            cy="54.74"
                            r="11.5106"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                    </g>

                    {/* FLOW12 */}
                    <g id="flow12-group" className="flow12">
                        <path
                            id="sparkle_bigger_11"
                            d="M24.276 20.549C24.8851 18.7666 27.3479 18.7126 28.07 20.387L28.1311 20.55L28.9531 22.9537C29.1414 23.5049 29.4458 24.0094 29.8457 24.433C30.2456 24.8566 30.7317 25.1895 31.2712 25.4093L31.4922 25.4918L33.8959 26.3128C35.6783 26.9218 35.7323 29.3846 34.0589 30.1067L33.8959 30.1679L31.4922 30.9898C30.9408 31.178 30.4361 31.4824 30.0123 31.8823C29.5885 32.2822 29.2555 32.7683 29.0356 33.3079L28.9531 33.5279L28.1321 35.9327C27.5231 37.7151 25.0603 37.7691 24.3392 36.0956L24.276 35.9327L23.4551 33.529C23.2669 32.9775 22.9625 32.4729 22.5626 32.0491C22.1627 31.6253 21.6766 31.2922 21.137 31.0723L20.917 30.9898L18.5132 30.1689C16.7298 29.5598 16.6758 27.097 18.3503 26.3759L18.5132 26.3128L20.917 25.4918C21.4682 25.3035 21.9727 24.9991 22.3963 24.5992C22.8199 24.1993 23.1528 23.7132 23.3726 23.1737L23.4551 22.9537 L24.276 20.549Z"
                            fill="white"
                        />
                        <circle
                            id="Ellipse_69_1"
                            cx="-4.58"
                            cy="21.71"
                            r="15.7432"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_70_2"
                            cx="43.98"
                            cy="55.06"
                            r="16.8036"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_71_3"
                            cx="42.29"
                            cy="-6.02"
                            r="14.4789"
                            fill="#5DA1E4"
                        />
                        <circle
                            id="Ellipse_68_4"
                            cx="53.36"
                            cy="15.4"
                            r="10.3595"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_72_5"
                            cx="9.57"
                            cy="10.24"
                            r="9.25831"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_73_6"
                            cx="0.39"
                            cy="31.41"
                            r="7.66767"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_74_7"
                            cx="24.4"
                            cy="54.91"
                            r="7.91239"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_75_8"
                            cx="12.32"
                            cy="43.81"
                            r="5.46526"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_76_9"
                            cx="21.32"
                            cy="0.27"
                            r="4.60876"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                        <circle
                            id="Ellipse_77_10"
                            cx="49.36"
                            cy="41.27"
                            r="10.3595"
                            fill="#E5F0FB"
                            fillOpacity="0.55"
                        />
                    </g>
                </g>
            </svg>
        </div>
    );
};
